/**
 * TrendChart — a Recharts trend line over the snapshot history for one metric, windowed by the chosen
 * period (YTD / 3-mo / 12-mo). Always wrapped in ResponsiveContainer; strokes/fills are token-colored
 * (no raw hex). Renders its own empty state when the period has no snapshots yet. Numerics stay LTR.
 */
import { useMemo } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useT } from "@/app";
import { token } from "@/design/theme";
import { formatMetric, seriesForPeriod, shortDate, type MetricMeta } from "../lib";
import type { HistoryMetric, Period } from "../types";

export function TrendChart({
  history,
  meta,
  period,
}: {
  history: ReadonlyArray<HistoryMetric>;
  meta: MetricMeta;
  period: Period;
}) {
  const { t } = useT();

  const data = useMemo(
    () =>
      seriesForPeriod(history, meta.key, period).map((h) => ({
        date: h.date,
        label: shortDate(h.date),
        value: h.value,
      })),
    [history, meta.key, period],
  );

  if (data.length === 0) {
    return (
      <div
        role="status"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--nv-s-1)",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 220,
          textAlign: "center",
          color: "var(--nv-slate)",
        }}
      >
        <span aria-hidden style={{ fontSize: "var(--nv-fs-h2)", color: "var(--nv-steel)" }}>
          ◷
        </span>
        <strong style={{ color: "var(--nv-ink)" }}>{t("analytics.trend.empty.title")}</strong>
        <p style={{ margin: 0, maxWidth: "42ch", fontSize: "var(--nv-fs-caption)" }}>
          {t("analytics.trend.empty.desc")}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ width: "100%", height: 240 }} dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke={token.lineFaint} vertical={false} />
            <XAxis dataKey="label" stroke={token.slate} fontSize={12} tickLine={false} />
            <YAxis allowDecimals stroke={token.slate} fontSize={12} width={44} />
            <Tooltip
              cursor={{ stroke: token.line }}
              formatter={(v: number | string) => formatMetric(Number(v), meta.format)}
              contentStyle={{
                background: token.surface,
                border: `1px solid ${token.line}`,
                borderRadius: token.rSm,
                color: token.ink,
              }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={token.azure}
              strokeWidth={2}
              dot={{ r: 2, fill: token.azure }}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p style={{ margin: "var(--nv-s-2) 0 0", fontSize: "var(--nv-fs-caption)", color: "var(--nv-steel)" }}>
        <span className="nv-ltr-island">{t("analytics.trend.points", { n: data.length })}</span>
      </p>
    </div>
  );
}
